import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEO from "@/components/base/SEO";

import type { BreadcrumbItem } from "@/components/base/Breadcrumb";

const faqs = [
  {
    q: "桃子视频是免费的吗？",
    a: "是的，桃子视频完全免费！我们聚合香蕉视频、西瓜视频、蜜桃视频、樱桃视频、芒果视频、蓝莓视频、草莓视频、柠檬视频、葡萄视频、荔枝视频等多家水果品牌影视资源，全部资源均可免费高清在线观看，无需付费订阅。",
  },
  {
    q: "桃子视频的画质怎么样？",
    a: "桃子视频提供多种清晰度选择，包括720P、1080P高清和4K超清画质。我们采用先进的CDN加速技术，确保播放流畅不缓冲。根据您的网络状况，系统会自动匹配最佳清晰度，为您带来极致的观影体验。",
  },
  {
    q: "桃子视频可以在手机上看吗？",
    a: "当然可以！桃子视频支持全平台观看，包括电脑端、手机浏览器、平板等设备。我们采用响应式设计，在不同屏幕尺寸上都能获得良好的观看体验。您可以在任何时间、任何地点，随时随地享受高清影视内容。",
  },
  {
    q: "桃子视频的影视资源更新快吗？",
    a: "非常快！我们与各大影视资源平台保持同步更新，热门电影、电视剧、综艺节目上线后24小时内即可在桃子视频观看。我们的团队全天候监控最新内容，确保您不会错过任何精彩。",
  },
  {
    q: "桃子视频需要注册吗？",
    a: "不需要！桃子视频无需注册即可直接观看所有内容。我们致力于为用户提供最简单便捷的观影体验，打开网站即可开始观看。没有任何繁琐的注册流程和会员限制。",
  },
  {
    q: "如何联系桃子视频团队？",
    a: "您可以通过以下方式联系我们：邮箱 contact@taozivideo.com，电话 400-888-6666，或访问我们的办公地址：北京市朝阳区桃子大厦15层。我们的客服团队工作日9:00-18:00在线，随时为您解答问题。",
  },
];

function AboutJsonLd() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "桃子视频",
    url: "https://www.taozivideo.com",
    description: "桃子视频聚合香蕉视频、西瓜视频、蜜桃视频、樱桃视频等水果品牌影视资源，免费高清在线观看电影、电视剧、综艺，更新快不缓冲。",
    email: "contact@taozivideo.com",
    telephone: "+86-400-888-6666",
    address: {
      "@type": "PostalAddress",
      streetAddress: "朝阳区桃子大厦15层",
      addressLocality: "北京",
      addressCountry: "CN",
    },
    sameAs: [
      "https://weibo.com/taozivideo",
      "https://www.taozivideo.com",
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

function FaqJsonLd() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export default function About() {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus("submitting");
    setFormError("");

    try {
      const formUrl = "https://readdy.ai/api/form/d9n1jpu8mbnljin59srg";
      const form = e.currentTarget as HTMLFormElement;
      const formData = new FormData(form);

      const honeypot = formData.get("phone_alt") as string;
      if (honeypot && honeypot.trim() !== "") {
        setFormStatus("success");
        setContactForm({ name: "", email: "", message: "" });
        setTimeout(() => setFormStatus("idle"), 3000);
        return;
      }
      formData.delete("phone_alt");

      const response = await fetch(formUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setContactForm({ name: "", email: "", message: "" });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || "提交失败，请稍后再试";
        setFormError(serverMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络后重试");
      setFormStatus("error");
    }
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "首页", href: "/" },
    { label: "关于我们" },
  ];

  return (
    <>
      <SEO
        title="关于我们 - 桃子视频 | 品牌介绍、口碑与联系方式"
        description="桃子视频品牌介绍——聚合香蕉视频、西瓜视频、蜜桃视频、樱桃视频等水果品牌影视资源，免费高清在线观看电影电视剧全集不缓冲。了解桃子视频的品牌故事、用户口碑与联系方式。"
        keywords="桃子视频,关于我们,品牌介绍,联系方式,影视平台"
        canonicalUrl="https://www.taozivideo.com/about"
      />
      <AboutJsonLd />
      <FaqJsonLd />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              {/* Brand Introduction */}
              <section className="mb-12 mt-6">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  关于桃子视频
                </h1>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      桃子视频成立于2024年，是中国领先的免费影视资源聚合平台。我们致力于整合优质影视内容，为用户提供一站式的在线观影体验。桃子视频聚合了<strong>香蕉视频、西瓜视频、蜜桃视频、樱桃视频、芒果视频、蓝莓视频、草莓视频、柠檬视频、葡萄视频、荔枝视频</strong>等十大水果品牌影视资源，涵盖电影、电视剧、综艺、动漫、纪录片等多个品类。
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      我们的使命是让每一位用户都能轻松找到想看的影视内容。凭借先进的资源聚合技术和智能推荐算法，桃子视频每天为数千万用户提供高清流畅的观影服务。无论您喜欢什么类型的影片，在桃子视频都能找到心仪的选择。
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      截至2026年，桃子视频已积累超过10万部影视资源，月活跃用户突破1.2亿，用户覆盖全国34个省级行政区。我们始终坚持"免费、高清、快速"的服务理念，不断优化产品体验，为用户带来更优质的观影享受。
                    </p>
                  </div>
                  <div className="rounded-xl overflow-hidden aspect-[4/3]">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20tech%20office%20with%20large%20video%20wall%20displaying%20colorful%20movie%20posters%2C%20warm%20orange%20and%20coral%20lighting%2C%20contemporary%20minimalist%20design%2C%20creative%20workspace%20atmosphere&width=800&height=600&seq=about-hero&orientation=landscape"
                      alt="桃子视频办公环境"
                      title="桃子视频办公环境"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </section>

              {/* Stats */}
              <section className="mb-12">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { value: "10万+", label: "影视资源", icon: "ri-movie-line" },
                    { value: "1.2亿+", label: "月活用户", icon: "ri-user-line" },
                    { value: "99.9%", label: "播放成功率", icon: "ri-check-double-line" },
                    { value: "10个", label: "合作品牌", icon: "ri-group-line" },
                  ].map((stat) => (
                    <div key={stat.label} className="p-6 rounded-xl bg-background-100/70 text-center">
                      <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                        <i className={`${stat.icon} text-lg text-accent-500`}></i>
                      </div>
                      <div className="text-xl md:text-2xl font-bold text-foreground-950 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Reputation / Testimonials */}
              <section className="mb-12">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">用户口碑</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    {
                      name: "王小明",
                      role: "影视爱好者",
                      comment: "用了桃子视频半年多了，资源真的很全。以前要在好几个APP之间切换找片子，现在一个网站全搞定了。画质也很清晰，基本不卡顿，强烈推荐！",
                      rating: 5,
                    },
                    {
                      name: "李婷婷",
                      role: "大学生",
                      comment: "作为学生党，免费+高清真的太香了！而且不需要注册就能看，打开就能用。追剧追综艺特别方便，更新速度也很快，新出的剧基本第二天就能看到。",
                      rating: 5,
                    },
                    {
                      name: "张伟",
                      role: "IT工程师",
                      comment: "从技术角度来说，桃子视频的CDN加速做得真的不错。我们家网速一般，但看1080P基本不缓冲。UI设计也很简洁，没有乱七八糟的广告弹窗，体验很好。",
                      rating: 5,
                    },
                  ].map((testimonial) => (
                    <div key={testimonial.name} className="p-5 rounded-xl bg-background-100/70">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: testimonial.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                        &ldquo;{testimonial.comment}&rdquo;
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                          <span className="text-xs font-bold text-accent-600">{testimonial.name[0]}</span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground-900">{testimonial.name}</p>
                          <p className="text-xs text-foreground-500">{testimonial.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-12">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">常见问题</h2>
                <div className="space-y-3">
                  {faqs.map((faq, idx) => (
                    <details key={idx} className="group rounded-xl bg-background-100/70 overflow-hidden">
                      <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                        <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</span>
                        <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                        <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                      </summary>
                      <div className="px-5 pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* Contact */}
              <section className="mb-12">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">联系我们</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">电子邮箱</p>
                        <p className="text-sm text-foreground-600">contact@taozivideo.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">客服电话</p>
                        <p className="text-sm text-foreground-600">400-888-6666</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">办公地址</p>
                        <p className="text-sm text-foreground-600">北京市朝阳区桃子大厦15层</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">工作时间</p>
                        <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>

                  <form
                    id="contact-form-about"
                    data-readdy-form
                    onSubmit={handleContactSubmit}
                    className="bg-background-100/70 rounded-xl p-6"
                  >
                    <h3 className="text-base font-bold text-foreground-950 mb-4">发送消息</h3>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="c-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          您的姓名
                        </label>
                        <input
                          id="c-name"
                          name="name"
                          type="text"
                          required
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="c-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电子邮箱
                        </label>
                        <input
                          id="c-email"
                          name="email"
                          type="email"
                          required
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div style={{ position: "absolute", left: "-9999px", opacity: 0 }}>
                        <input
                          name="phone_alt"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <div>
                        <label htmlFor="c-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="c-message"
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={formStatus === "submitting"}
                        className="w-full py-2.5 rounded-lg bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {formStatus === "submitting" ? "发送中..." : "发送消息"}
                      </button>
                      {formStatus === "success" && (
                        <p className="text-sm text-green-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                      )}
                      {formStatus === "error" && (
                        <p className="text-sm text-red-500 text-center">{formError}</p>
                      )}
                    </div>
                  </form>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}